import { t as uiText } from '../../i18n';
import type { SetupMessageKey } from '../../../functions/src/agentAdvisor/types';

export const CARD_STARTERS: Record<string, string> = {
  get ingest() { return uiText("I want to import this site's knowledge"); },
  get embedSecurity() { return uiText("Register a host site and issue a token"); },
  get appearance() { return uiText("Choose the look of public AI"); },
  get contact() { return uiText("I want to set the contact path"); },
};

export type Day0CardKey = 'ingest' | 'embedSecurity' | 'appearance' | 'contact';
export type Day0PanelKey = 'embedSecurity' | 'appearance' | 'contact';

/**
 * Chips and card titles that must open the host-site panel without asking the model.
 * Free-text phrasings are routed server-side by hostSiteIntent.ts instead.
 */
export function isEmbedSecurityStarter(text: string): boolean {
  const t = text.trim();
  return t === CARD_STARTERS.embedSecurity || t === uiText("Register a host site");
}

export interface Day0ActionCard {
  key: Day0CardKey;
  title: string;
  description: string;
  icon: string;
  isComplete: boolean;
}

export interface Day0SetupFlags {
  lastCrawledAt: string | null;
  embedReady: boolean;
  appearanceSaved: boolean;
  contactPathDecided: boolean;
}

export function buildDay0ActionCards(flags: Day0SetupFlags): Day0ActionCard[] {
  return [
    {
      key: 'ingest',
      title: uiText("Import knowledge"),
      description: uiText("Crawl the site, or create custom answers"),
      icon: '📥',
      isComplete: flags.lastCrawledAt !== null,
    },
    {
      key: 'embedSecurity',
      title: uiText("Register a host site"),
      description: uiText("Register the site URL and issue a token"),
      icon: '🔑',
      isComplete: flags.embedReady,
    },
    {
      key: 'appearance',
      title: uiText("Choose the look"),
      description: uiText("Choose colors, buttons, and the widget"),
      icon: '🎨',
      isComplete: flags.appearanceSaved,
    },
    {
      key: 'contact',
      title: uiText("Set the contact path"),
      description: uiText("Choose the phone number or link shown after the answer"),
      icon: '📞',
      isComplete: flags.contactPathDecided,
    },
  ];
}

/**
 * Which card the Recommendation Engine's messageKey points at.
 * The engine picks the next step (functions/src/agentAdvisor/recommendation); this only maps
 * that choice onto a card so the two cannot drift into separate funnel orders.
 */
const MESSAGE_KEY_TO_CARD: Partial<Record<SetupMessageKey, Day0CardKey>> = {
  setup_data_ingestion: 'ingest',
  issue_embed: 'embedSecurity',
  setup_appearance: 'appearance',
  setup_contact_path: 'contact',
};

export function buildDay0Recommendations(
  flags: Day0SetupFlags,
  /** messageKey from the latest SetupProposal, when the home has one. */
  nextMessageKey?: SetupMessageKey
): { content: string; choices: string[] } {
  const cards = buildDay0ActionCards(flags);
  const incomplete = cards.filter((c) => !c.isComplete);
  const picks = incomplete.length > 0 ? incomplete : cards;

  // Lead with the step the engine chose; keep the rest as escape hatches.
  const leadKey = nextMessageKey ? MESSAGE_KEY_TO_CARD[nextMessageKey] : undefined;
  const ordered = leadKey
    ? [...picks].sort((a, b) => Number(b.key === leadKey) - Number(a.key === leadKey))
    : picks;

  return {
    content: incomplete.length > 0
      ? uiText("Switching tasks. Next, start with initial setup.")
      : uiText("Initial setup is complete. Choose what you want to do next."),
    choices: ordered.slice(0, 4).map((c) => CARD_STARTERS[c.key] ?? c.title),
  };
}

export function knowledgeStatusLabel(lastCrawledAt: string | null): string {
  return lastCrawledAt ? uiText("Imported") : uiText("Not imported");
}
